import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Bot, Plus, Trash2, Save, Settings as SettingsIcon, Megaphone, AlertCircle, Power, Link2, Users, Brain, Coins } from 'lucide-react';
import { Link } from 'react-router-dom';
import BotSettingsPanel from '../components/BotSettingsPanel';
import BotWebhookManager from '../components/BotWebhookManager';
import UserManagement from '../components/UserManagement';
import PageContent from '../components/PageContent';
import AISettings from '../components/AISettings';
import AlertsMonitoring from '../components/AlertsMonitoring';
import { useLanguage } from '../lib/i18n';

type TradingBot = {
  id: string;
  name: string;
  description: string;
  risk_level: 'low' | 'medium' | 'high';
  pnl_percentage: number;
  running_days: number;
  webhook_url: string;
  is_active: boolean;
  show_on_public: boolean;
  created_at: string;
};

function AdminDashboard() {
  const [bots, setBots] = useState<TradingBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingBot, setEditingBot] = useState<Partial<TradingBot> | null>(null);
  const [showSettings, setShowSettings] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'bots' | 'users' | 'pages' | 'ai' | 'alerts'>('bots');
  const { t } = useLanguage();

  useEffect(() => {
    loadBots();
  }, []);

  const loadBots = async () => {
    try {
      const { data, error } = await supabase
        .from('trading_bots')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setBots(data || []);
    } catch (error: any) {
      toast.error('Failed to load trading bots');
      console.error('Error:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBot = () => {
    const webhookKey = crypto.randomUUID();
    
    setEditingBot({
      name: '',
      description: '',
      risk_level: 'low',
      pnl_percentage: 0,
      running_days: 0,
      webhook_url: webhookKey,
      is_active: true,
      show_on_public: false
    });
  };

  const handleSaveBot = async () => {
    if (!editingBot?.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('trading_bots')
        .upsert({
          ...editingBot,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      toast.success(editingBot.id ? 'Bot updated successfully' : 'Bot created successfully');
      setEditingBot(null);
      await loadBots();
    } catch (error: any) {
      toast.error('Failed to save bot');
      console.error('Error:', error.message);
    }
  };

  const handleDeleteBot = async (id: string) => {
    if (!confirm('Are you sure you want to delete this bot?')) return;

    try {
      const { error } = await supabase
        .from('trading_bots')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Bot deleted successfully');
      await loadBots();
    } catch (error: any) {
      toast.error('Failed to delete bot');
      console.error('Error:', error.message);
    }
  };

  const handleToggleBot = async (bot: TradingBot) => {
    try {
      // First get admin's trading account
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('trading_account, trading_enabled')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      // Check if trading is properly configured
      if (!profile?.trading_enabled || !profile?.trading_account?.api_key) {
        toast.error('Please configure your exchange settings first');
        return;
      }

      // Update bot status
      const { error } = await supabase
        .from('trading_bots')
        .update({ 
          is_active: !bot.is_active,
          trading_account: bot.is_active ? null : {
            ...profile.trading_account,
            updated_at: new Date().toISOString()
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', bot.id);

      if (error) throw error;
      
      // If activating bot, start trading
      if (!bot.is_active) {
        // Create bot status record
        const { error: statusError } = await supabase
          .from('user_bot_status')
          .upsert({
            user_id: user.id,
            bot_id: bot.id,
            is_trading: true,
            is_selected: true,
            error_count: 0,
            last_error: null,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,bot_id'
          });

        if (statusError) throw statusError;
      } else {
        // Stop trading
        const { error: statusError } = await supabase
          .from('user_bot_status')
          .update({
            is_trading: false,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('bot_id', bot.id);

        if (statusError) throw statusError;
      }
      
      toast.success(`Bot ${bot.is_active ? 'deactivated' : 'activated'} successfully`);
      await loadBots();
    } catch (error: any) {
      toast.error('Failed to update bot status');
      console.error('Error:', error.message);
    }
  };

  const handleTogglePublic = async (bot: TradingBot) => {
    try {
      const { error } = await supabase
        .from('trading_bots')
        .update({ 
          show_on_public: !bot.show_on_public,
          updated_at: new Date().toISOString()
        })
        .eq('id', bot.id);

      if (error) throw error;
      
      toast.success(`Bot ${bot.show_on_public ? 'hidden from' : 'shown on'} public page`);
      await loadBots();
    } catch (error: any) {
      toast.error('Failed to update bot visibility');
      console.error('Error:', error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-white flex items-center space-x-2">
          <Bot className="h-6 w-6" />
          <span>Admin Dashboard</span>
        </h2>
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('bots')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'bots'
                ? 'bg-cyan-500 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            <Bot className="h-5 w-5" />
            <span>Trading Bots</span>
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'alerts'
                ? 'bg-cyan-500 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            <AlertCircle className="h-5 w-5" />
            <span>Alerts & Errors</span>
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'users'
                ? 'bg-cyan-500 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            <Users className="h-5 w-5" />
            <span>Users</span>
          </button>
          <button
            onClick={() => setActiveTab('pages')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'pages'
                ? 'bg-cyan-500 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            <Megaphone className="h-5 w-5" />
            <span>Pages & Announcements</span>
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'ai'
                ? 'bg-cyan-500 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            <Brain className="h-5 w-5" />
            <span>AI Settings</span>
          </button>
        </div>
      </div>

      {activeTab === 'alerts' && <AlertsMonitoring />}
      {activeTab === 'users' && <UserManagement />}
      {activeTab === 'pages' && <PageContent />}
      {activeTab === 'ai' && <AISettings />}
      {activeTab === 'bots' && (
        <div>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Manage Trading Bots</h2>
            <button
              onClick={handleAddBot}
              className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Add Bot</span>
            </button>
          </div>

          {/* New Bot Form */}
          {editingBot && !editingBot.id && (
            <div className="mt-6 bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
              <h3 className="text-xl font-semibold text-white mb-6">New Bot</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Bot Name *
                  </label>
                  <input
                    type="text"
                    value={editingBot.name || ''}
                    onChange={(e) => setEditingBot({ ...editingBot, name: e.target.value })}
                    className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                    placeholder="Enter bot name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Risk Level
                  </label>
                  <select
                    value={editingBot.risk_level || 'low'}
                    onChange={(e) => setEditingBot({ ...editingBot, risk_level: e.target.value as 'low' | 'medium' | 'high' })}
                    className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                  >
                    <option value="low">Low Risk</option>
                    <option value="medium">Medium Risk</option>
                    <option value="high">High Risk</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editingBot.description || ''}
                    onChange={(e) => setEditingBot({ ...editingBot, description: e.target.value })}
                    className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                    rows={3}
                    placeholder="Enter bot description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Status
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editingBot.is_active}
                      onChange={(e) => setEditingBot({ ...editingBot, is_active: e.target.checked })}
                      className="h-4 w-4 text-cyan-500"
                    />
                    <span className="text-gray-200">Active</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setEditingBot(null)}
                  className="px-4 py-2 text-white hover:text-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBot}
                  className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
                >
                  <Save className="h-5 w-5" />
                  <span>Save Bot</span>
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 gap-6">
            {bots.map((bot) => (
              <React.Fragment key={bot.id}>
                <div
                  className={`bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border ${
                    bot.is_active ? 'border-blue-500/20' : 'border-gray-500/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Bot className={`h-8 w-8 ${
                        bot.risk_level === 'low' ? 'text-green-400' :
                        bot.risk_level === 'medium' ? 'text-yellow-400' :
                        'text-red-400'
                      }`} />
                      <div>
                        <h3 className="text-xl font-semibold text-white">{bot.name}</h3>
                        <p className="text-gray-300">{bot.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleTogglePublic(bot)}
                        className={`p-2 rounded-full transition-colors ${
                          bot.show_on_public
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                            : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                        }`}
                        title={bot.show_on_public ? 'Hide from Public' : 'Show on Public'}
                      >
                        <Megaphone className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleToggleBot(bot)}
                        className={`p-2 rounded-full transition-colors ${
                          bot.is_active
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                            : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                        }`}
                        title={bot.is_active ? 'Deactivate Bot' : 'Activate Bot'}
                      >
                        <Power className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setShowSettings(showSettings === bot.id ? null : bot.id)}
                        className="p-2 text-gray-400 hover:text-white transition-colors"
                      >
                        <SettingsIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setEditingBot(bot)}
                        className="p-2 text-gray-400 hover:text-white transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteBot(bot.id)}
                        className="p-2 text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-400">Risk Level</p>
                      <p className={`text-lg font-semibold capitalize ${
                        bot.risk_level === 'low' ? 'text-green-400' :
                        bot.risk_level === 'medium' ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {bot.risk_level}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">PnL</p>
                      <p className={`text-lg font-semibold ${bot.pnl_percentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {bot.pnl_percentage >= 0 ? '+' : ''}{bot.pnl_percentage}%
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Running Days</p>
                      <p className="text-lg font-semibold text-white">{bot.running_days}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Status</p>
                      <p className={`text-lg font-semibold ${bot.is_active ? 'text-green-400' : 'text-yellow-400'}`}>
                        {bot.is_active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <BotWebhookManager bot={bot} onUpdate={loadBots} isAdmin={true} />
                  </div>
                </div>

                {/* Edit Form - Shown directly under the bot card when editing */}
                {editingBot?.id === bot.id && (
                  <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
                    <h3 className="text-xl font-semibold text-white mb-6">Edit Bot</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">
                          Bot Name *
                        </label>
                        <input
                          type="text"
                          value={editingBot.name || ''}
                          onChange={(e) => setEditingBot({ ...editingBot, name: e.target.value })}
                          className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                          placeholder="Enter bot name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">
                          Risk Level
                        </label>
                        <select
                          value={editingBot.risk_level || 'low'}
                          onChange={(e) => setEditingBot({ ...editingBot, risk_level: e.target.value as 'low' | 'medium' | 'high' })}
                          className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                        >
                          <option value="low">Low Risk</option>
                          <option value="medium">Medium Risk</option>
                          <option value="high">High Risk</option>
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-200 mb-2">
                          Description
                        </label>
                        <textarea
                          value={editingBot.description || ''}
                          onChange={(e) => setEditingBot({ ...editingBot, description: e.target.value })}
                          className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                          rows={3}
                          placeholder="Enter bot description"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">
                          Status
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editingBot.is_active}
                            onChange={(e) => setEditingBot({ ...editingBot, is_active: e.target.checked })}
                            className="h-4 w-4 text-cyan-500"
                          />
                          <span className="text-gray-200">Active</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">
                          Show on Public Page
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={editingBot.show_on_public}
                            onChange={(e) => setEditingBot({ ...editingBot, show_on_public: e.target.checked })}
                            className="h-4 w-4 text-cyan-500"
                          />
                          <span className="text-gray-200">Public</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-4 mt-6">
                      <button
                        onClick={() => setEditingBot(null)}
                        className="px-4 py-2 text-white hover:text-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveBot}
                        className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
                      >
                        <Save className="h-5 w-5" />
                        <span>Save Changes</span>
                      </button>
                    </div>
                  </div>
                )}

                {showSettings === bot.id && (
                  <BotSettingsPanel bot={bot} onClose={() => setShowSettings(null)} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
